import { ReactNode, useEffect, useState } from "react";
import { CombinedInputProps } from "./Input";
import styles from "./Autocomplete.module.scss";
import { SleekInput } from "./SleekInput";

interface AutocompleteProps<T> {
    onAutocomplete: (value: string) => Promise<T[] | null>;
    onAutocompleteSelected: (item: T) => void;
    autocompleteOptionKey: (item: T) => string;
    autocompleteDisplayOption: (item: T) => ReactNode;
    searchingText?: ReactNode;
    autoClear?: boolean;
    delay?: number;
    onChange?: never;
}

type CombinedAutocompleteProps<T> = AutocompleteProps<T> & CombinedInputProps;

export const Autocomplete = <TAutocompleteItem,>(props: CombinedAutocompleteProps<TAutocompleteItem>) => {
    const {
        value,
        onAutocomplete,
        onAutocompleteSelected,
        autocompleteDisplayOption,
        autocompleteOptionKey,
        searchingText,
        delay,
        onChange,
        autoClear,
        ...rest
    } = props;

    const [text, setText] = useState<string>(value?.toString() ?? "");
    const [typing, setTyping] = useState<boolean>(false);
    const [suggestions, setSuggestions] = useState<TAutocompleteItem[] | null>(null);

    const handleInputChanged = async (e: React.ChangeEvent<HTMLInputElement>) => {
        setTyping(true);
        setText(e.target.value);
    };

    const handleAutocompleteSelected = (item: TAutocompleteItem) => {
        setTyping(false);
        onAutocompleteSelected(item);
        if (autoClear) {
            setText("");
            setSuggestions(null);
        }
    };

    useEffect(() => {
        setTyping(false);
        setText(value?.toString() ?? "");
        setSuggestions([]);
    }, [value]);

    useEffect(() => {
        if (!typing) return;

        const debounce = setTimeout(async () => {
            setSuggestions(await onAutocomplete(text));
        }, delay ?? 1000);

        return () => clearTimeout(debounce);
    }, [text, typing]);

    return (
        <>
            <SleekInput
                onChange={e => handleInputChanged(e)}
                value={text}
                autoComplete="off"
                {...rest}
            />

            {!!searchingText && !suggestions?.length && typing && (
                <div>
                    {searchingText}
                </div>
            )}

            {!!suggestions?.length && (
                <div>
                    {suggestions.map(s => (
                        <div
                            tabIndex={0}
                            key={autocompleteOptionKey(s)}
                            onClick={() => handleAutocompleteSelected(s)}
                            onKeyUp={keyEvent => keyEvent.key === "Enter" && handleAutocompleteSelected(s)}
                            className={styles.suggestion}
                        >
                            {autocompleteDisplayOption(s)}
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};